import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'sk_admin_page_visibility';

// Pages that can NEVER be disabled (core admin/system pages)
const ALWAYS_ON_PAGES = new Set(['dashboard', 'admin-monitoring', 'admin-schedule', 'privacy']);

export interface PageVisibilityState {
  [pageId: string]: boolean; // true = online, false = offline
}

/**
 * Returns the current page visibility map from localStorage.
 * Pages default to `true` (online) if not explicitly set.
 */
const readFromStorage = (): PageVisibilityState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore parse errors
  }
  return {};
};

const writeToStorage = (state: PageVisibilityState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
};

// Custom event name so multiple components stay in sync
const SYNC_EVENT = 'sk_page_visibility_changed';

/**
 * Hook to manage page online/offline toggles.
 * Persists to localStorage and syncs across components via a custom event.
 */
const usePageVisibility = () => {
  const [visibility, setVisibility] = useState<PageVisibilityState>(() => {
    const initial = readFromStorage();
    console.log('🎬 usePageVisibility initialized with state:', initial);
    return initial;
  });
  
  const [needsSync, setNeedsSync] = useState(false);

  // Dispatch sync event AFTER state updates
  useEffect(() => {
    if (needsSync) {
      console.log('📤 Dispatching sync event with visibility:', visibility);
      window.dispatchEvent(new Event(SYNC_EVENT));
      setNeedsSync(false);
    }
  }, [needsSync, visibility]);

  // Listen for changes from other components / tabs
  useEffect(() => {
    const handler = () => {
      const newState = readFromStorage();
      console.log('📢 Page visibility sync event received, new state:', newState);
      setVisibility(newState);
    };
    window.addEventListener(SYNC_EVENT, handler);
    window.addEventListener('storage', handler); // cross-tab sync
    return () => {
      window.removeEventListener(SYNC_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const isPageOnline = useCallback(
    (pageId: string): boolean => {
      // Strip leading # if present
      const id = pageId.replace(/^#/, '');
      if (ALWAYS_ON_PAGES.has(id)) return true;
      return visibility[id] !== false; // default = online
    },
    [visibility]
  );

  const togglePage = useCallback((pageId: string) => {
    const id = pageId.replace(/^#/, '');
    if (ALWAYS_ON_PAGES.has(id)) return; // can't toggle always-on pages
    setVisibility(prev => {
      const currentlyOnline = prev[id] !== false;
      const next = { ...prev, [id]: !currentlyOnline };
      console.log('🔄 Toggle page:', id, 'from', currentlyOnline, 'to', !currentlyOnline, 'new state:', next);
      writeToStorage(next);
      return next;
    });
    setNeedsSync(true);
  }, []);

  const setPageOnline = useCallback((pageId: string, online: boolean) => {
    const id = pageId.replace(/^#/, '');
    if (ALWAYS_ON_PAGES.has(id)) return;
    setVisibility(prev => {
      const next = { ...prev, [id]: online };
      writeToStorage(next);
      return next;
    });
    setNeedsSync(true);
  }, []);

  const canToggle = useCallback((pageId: string): boolean => {
    const id = pageId.replace(/^#/, '');
    return !ALWAYS_ON_PAGES.has(id);
  }, []);

  return { visibility, isPageOnline, togglePage, setPageOnline, canToggle };
};

export { usePageVisibility };
export default usePageVisibility;
